import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

/**
 * DatePicker — shadcn Popover + Calendar wrapper that always renders in
 * dd/mm/yyyy regardless of the browser/OS locale. Bypasses the
 * inconsistent styling of native <input type="date"> and gives us the
 * exact placeholder-string business requested.
 *
 * Props:
 *   • value       ISO date string "YYYY-MM-DD" (or empty)
 *   • onChange    (isoString | null) => void
 *   • placeholder default: "dd/mm/yyyy"
 *   • className   extra classes for the trigger button
 *   • testId      data-testid propagated to the trigger button
 *   • clearable   show a small × to reset (default true)
 *   • disabled    disable interactions
 */
export default function DatePicker({
  value = "",
  onChange,
  placeholder = "dd/mm/yyyy",
  className = "",
  testId,
  clearable = true,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);

  const parsedDate = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const displayValue = parsedDate && isValid(parsedDate) ? format(parsedDate, "dd/MM/yyyy") : "";

  const handleSelect = (date) => {
    if (!date) {
      onChange?.(null);
      setOpen(false);
      return;
    }
    // Store as local ISO date (YYYY-MM-DD) — no TZ math since the calendar
    // hands us the day the user tapped.
    const iso = format(date, "yyyy-MM-dd");
    onChange?.(iso);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full flex items-center justify-between gap-2 bg-transparent border-b border-black/15 dark:border-white/15 py-2 text-left outline-none focus:border-black dark:focus:border-white transition-colors text-sm",
            !displayValue && "text-neutral-400",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
        >
          <span className="tabular-nums">
            {displayValue || placeholder}
          </span>
          <span className="flex items-center gap-1 text-neutral-400">
            {clearable && displayValue && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="hover:text-red-600 dark:hover:text-rose-400 transition-colors p-0.5"
                aria-label="Clear date"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <XIcon size={13} />
              </span>
            )}
            <CalendarIcon size={14} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Calendar
          mode="single"
          weekStartsOn={1}
          initialFocus
          selected={parsedDate && isValid(parsedDate) ? parsedDate : undefined}
          defaultMonth={parsedDate && isValid(parsedDate) ? parsedDate : undefined}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}
